/* eslint-disable no-unused-vars */
import React from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import styled, { css } from 'styled-components';
import { SvgWaveTop } from '../vectors/Waves';
import { HeadingSection, SubtitleSection } from '../styles/Typography';
import { ButtonNormal, IconButtonAnchor } from '../ui/Buttons';
import { FacebookIcon, InstagramIcon } from '../vectors/Social';
import { FooterLogo } from '../vectors/Logo';
import {
  ButterflyDrawing,
  AppleDrawing,
  LemonDrawing,
  FishesDrawing,
} from '../vectors/Drawings';
import { usePageLocale } from '../../hooks/usePageLocale';
import { useDefaultLocale } from '../../hooks/useDefaultLocale';

const sharedDrawingsCss = css`
  z-index: 1;
  opacity: 0.3;
  position: absolute;
`;

const FooterWrapper = styled.footer`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  /* Butterfly */
  & svg:nth-of-type(2) {
    ${sharedDrawingsCss}
    top: 15%;
    left: 10px;
  }

  /* Apple */
  & svg:nth-of-type(3) {
    ${sharedDrawingsCss}
    top: 55%;
    transform: scale(2);
    left: 10px;

    @media screen and (max-width: 767px) {
      top: 70%;
    }
  }

  /* Lemon */
  & svg:nth-of-type(4) {
    ${sharedDrawingsCss}
    top: 10%;
    transform: scale(1.2);
    right: -50px;

    @media screen and (max-width: 1000px) {
      display: none;
    }
  }

  /* Fishes */
  & svg:nth-of-type(5) {
    ${sharedDrawingsCss}
    top: 20%;
    left: 45%;

    @media screen and (max-width: 767px) {
      top: 30%;
    }
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gold-2);
  padding: var(--gap-2) var(--padding) var(--gap-2) var(--padding);

  @media screen and (max-width: 600px) {
    padding: var(--gap-2) var(--padding) var(--gap-4) var(--padding);
  }
`;

const ContentContainer = styled.div`
  width: ${({ fullWidth }) =>
    fullWidth ? '100%' : 'var(--layout-desktop-container)'};
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media screen and (max-width: 1240px) {
    width: 100%;
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    row-gap: var(--gap-4);
  }
`;

const LeftContainer = styled.div`
  display: grid;
  row-gap: var(--gap-5);

  @media screen and (max-width: 600px) {
    row-gap: var(--gap-4);

    & h1,
    p {
      text-align: center;
      max-width: unset;
    }
  }
`;

const CopyrightWrapper = styled.div`
  background: var(--gold-2);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--padding) var(--gap-4) var(--padding);
`;

const CopyrightContainer = styled.div`
  width: ${({ fullWidth }) =>
    fullWidth ? '100%' : 'var(--layout-desktop-container)'};
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media screen and (max-width: 1240px) {
    width: 100%;
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    row-gap: var(--gap-4);
  }
`;

const CopyrightLeftContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, min-content);
  column-gap: var(--gap-4);
  color: white;
  align-items: center;

  @media screen and (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: var(--gap-5);
  }
`;

const sharedCopyrightText = css`
  color: white;
  font-size: var(--font-size-micro-1);
  white-space: nowrap;
  height: min-content;
`;

const CopyrightText = styled.div`
  ${sharedCopyrightText}

  @media screen and (max-width: 600px) {
    justify-content: center;
    display: grid;
    font-size: var(--font-size);
  }
`;

const CopyrightLink = styled(Link)`
  ${sharedCopyrightText}

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (max-width: 600px) {
    text-align: center;
  }
`;

const CopyrightAnchorLink = styled(Link)`
  ${sharedCopyrightText}

  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (max-width: 600px) {
    text-align: center;
  }
`;

const ButtonsContainer = styled.div`
  display: grid;
  z-index: 1;
  grid-template-columns: repeat(2, min-content);
  column-gap: ${({ isSocialContainer }) =>
    isSocialContainer ? 'var(--gap-4)' : ' var(--gap-5)'};
  justify-content: ${({ isSocialContainer }) => isSocialContainer && 'end'};

  & svg {
    width: 30px;
    height: 30px;
  }

  @media screen and (max-width: 767px) {
    justify-content: ${({ isSocialContainer }) => isSocialContainer && 'end'};
    justify-items: ${({ isSocialContainer }) => !isSocialContainer && 'center'};
    grid-template-columns: ${({ isSocialContainer }) =>
      !isSocialContainer && '1fr'};
    row-gap: ${({ isSocialContainer }) => !isSocialContainer && 'var(--gap-5)'};
  }

  @media screen and (max-width: 600px) {
    justify-content: ${({ isSocialContainer }) =>
      isSocialContainer && 'center'};

    grid-row: ${({ isSocialContainer }) => isSocialContainer && '1'};
  }
`;

const ArtworkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  & svg {
    width: 250px;
    height: 230px;
  }

  @media screen and (max-width: 600px) {
    justify-content: center;
    grid-row: 1;

    & svg {
      width: 200px;
      height: 180px;
    }
  }
`;

export const Footer = ({ fullWidth, footerRef }) => {
  const { locale: pageLocale } = usePageLocale();

  const data = useStaticQuery(graphql`
    {
      allDatoCmsFooter {
        nodes {
          locale
          title
          subtitle
        }
      }
    }
  `);

  const filteredNodes = data.allDatoCmsFooter.nodes.find(
    ({ locale }) => locale === pageLocale
  );

  const { locale } = usePageLocale();
  const { defaultLocale } = useDefaultLocale();

  return (
    <FooterWrapper ref={footerRef}>
      <SvgWaveTop waveFill="var(--gold-2)" />
      <ButterflyDrawing />
      <AppleDrawing />
      <LemonDrawing />
      <FishesDrawing />
      <ContentWrapper>
        <ContentContainer fullWidth={fullWidth}>
          <LeftContainer>
            <HeadingSection blueHeading>{filteredNodes.title}</HeadingSection>
            <SubtitleSection gold>{filteredNodes.subtitle}</SubtitleSection>
          </LeftContainer>
          <ArtworkContainer>
            <FooterLogo />
          </ArtworkContainer>
        </ContentContainer>
      </ContentWrapper>
      <CopyrightWrapper>
        <CopyrightContainer fullWidth={fullWidth}>
          <CopyrightLeftContainer>
            <CopyrightText>© La Rosa dei gusti</CopyrightText>
            <CopyrightAnchorLink
              to={
                locale === defaultLocale
                  ? '/privacy-policy'
                  : `/${locale}/privacy-policy`
              }
            >
              Privacy & Cookie Policy
            </CopyrightAnchorLink>
          </CopyrightLeftContainer>
          <ButtonsContainer isSocialContainer>
            <IconButtonAnchor
              ariaLabel="Instagram"
              iconLink="https://www.instagram.com/larosadeigusti/"
              icon={<InstagramIcon />}
            />
            <IconButtonAnchor
              ariaLabel="Facebook"
              iconLink="https://www.facebook.com/larosadeigusti/"
              icon={<FacebookIcon />}
            />
          </ButtonsContainer>
        </CopyrightContainer>
      </CopyrightWrapper>
    </FooterWrapper>
  );
};
